import { CtxDataFile, FileAction } from 'interface/file/file.interface';
import { FILE_STATUS } from 'pages/ExperienceCanvas/types';
import { FILE_STATUSES } from 'components/ExperienceCanvas/constants';
import { getHttpErrorExceptionMessage, isHttpConflictError } from 'helpers/httpError.helper';

export const PROCESSING_QUEUED = 'PROCESSING_QUEUED';

export type StatusGroup = 'processing' | 'paused' | 'ready' | 'failed';

export const STATUS_LABELS: Record<string, string> = {
  [FILE_STATUS.PROCESSING_SUCCESS]: 'Processed',
  [FILE_STATUS.PROCESSING]: 'Processing',
  [PROCESSING_QUEUED]: 'In Queue',
  [FILE_STATUS.PROCESSING_PAUSED]: 'Paused',
  [FILE_STATUS.UPLOADED]: 'Ready To Process',
  [FILE_STATUS.UPLOADING]: 'Uploading',
  [FILE_STATUS.PROCESSING_FAILED]: 'Failed To Process',
  [FILE_STATUS.UPLOADING_FAILED]: 'Failed To Upload',
};

export const STATUS_PRIORITY: Record<string, number> = {
  [FILE_STATUS.PROCESSING]: 1,
  [PROCESSING_QUEUED]: 1,
  [FILE_STATUS.PROCESSING_PAUSED]: 2,
  [FILE_STATUS.PROCESSING_FAILED]: 3,
  [FILE_STATUS.UPLOADING_FAILED]: 3,
  [FILE_STATUS.UPLOADED]: 4,
  [FILE_STATUS.UPLOADING]: 5,
  [FILE_STATUS.PROCESSING_SUCCESS]: 6,
};

export const PILL_DEFINITIONS: { key: string; label: string; status?: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'processing', label: 'Processing', status: FILE_STATUS.PROCESSING },
  { key: 'paused', label: 'Paused', status: FILE_STATUS.PROCESSING_PAUSED },
  { key: 'failed', label: 'Failed', status: FILE_STATUS.PROCESSING_FAILED },
  { key: 'ready', label: 'Ready', status: FILE_STATUS.UPLOADED },
  { key: 'processed', label: 'Processed', status: FILE_STATUS.PROCESSING_SUCCESS },
  { key: 'uploading', label: 'Uploading', status: FILE_STATUS.UPLOADING },
];

export function classifyStatus(status: string): StatusGroup | null {
  if (status === FILE_STATUS.PROCESSING || status === PROCESSING_QUEUED) return 'processing';
  if (status === FILE_STATUS.PROCESSING_PAUSED) return 'paused';
  if (status === FILE_STATUS.UPLOADED) return 'ready';
  if (status === FILE_STATUS.PROCESSING_FAILED || status === FILE_STATUS.UPLOADING_FAILED) return 'failed';
  return null;
}

export function isFailedStatus(status: string) {
  return status === FILE_STATUS.PROCESSING_FAILED || status === FILE_STATUS.UPLOADING_FAILED;
}

export function isTerminalFileStatus(status: string) {
  return (
    status === FILE_STATUS.PROCESSING_SUCCESS ||
    status === FILE_STATUS.PROCESSING_FAILED ||
    status === FILE_STATUS.UPLOADING_FAILED
  );
}

export function formatErrorCount(count: number) {
  return `${count} error${count !== 1 ? 's' : ''}`;
}

export function formatPartialFailureSummary(processed: number, total: number, failed: number) {
  return `${processed}/${total} Processed (${formatErrorCount(failed)})`;
}

const CONFLICT_MESSAGES: Record<FileAction, string> = {
  PROCESS: 'Process already started by another user.',
  PAUSE: 'Pause could not be applied because processing was changed by another user.',
  RESUME: 'Resume could not be applied because processing was changed by another user.',
};

export function getFileActionErrorSnackbarContent(action: FileAction, err: unknown): string {
  if (isHttpConflictError(err)) {
    return getHttpErrorExceptionMessage(err) ?? CONFLICT_MESSAGES[action];
  }
  return `Failed to ${action.toLowerCase()} files. Please try again.`;
}

type TriggerTooltipParams = {
  files: CtxDataFile[];
  counts: { allCompleted: boolean; hasProcessing: boolean; queued: number };
  processed: number;
  failed: number;
  ready: number;
  total: number;
  showProcessingUi: boolean;
};

export function getTriggerTooltipText({
  files,
  counts,
  processed,
  failed,
  ready,
  total,
  showProcessingUi,
}: TriggerTooltipParams): string {
  if (total === 0 && files.length === 0) return 'No files uploaded';
  if (counts.allCompleted && failed === 0) return 'Processing complete';

  if (showProcessingUi) {
    if (failed > 0) {
      return `Processing in progress, ${processed} of ${total} processed, ${formatErrorCount(failed)}`;
    }
    const activelyProcessing = files.filter((f) => f.status === FILE_STATUS.PROCESSING).length;
    if (activelyProcessing && counts.queued) {
      return `Processing in progress, ${processed} of ${total} processed, ${counts.queued} file${
        counts.queued !== 1 ? 's' : ''
      } in queue`;
    }
    if (activelyProcessing) {
      return `Processing in progress, ${processed} of ${total} processed`;
    }
    return `${counts.queued} file${counts.queued !== 1 ? 's' : ''} in queue`;
  }

  if (processed > 0 && failed > 0) {
    return formatPartialFailureSummary(processed, total, failed);
  }

  if (ready > 0 && failed > 0) {
    return `${formatErrorCount(failed)}, ${ready} file${ready !== 1 ? 's' : ''} ready to process`;
  }

  if (failed > 0) {
    return formatPartialFailureSummary(processed, total, failed);
  }

  if (ready > 0) {
    return ready === 1 ? '1 file is ready to process' : `${ready} files are ready to process`;
  }

  const firstStatus = files[0]?.status as FILE_STATUS | undefined;
  if (firstStatus && FILE_STATUSES[firstStatus]) {
    return FILE_STATUSES[firstStatus].text;
  }
  return FILE_STATUSES.UNKNOWN.text;
}
